import { useEmbeddedInApp } from "@/contexts/EmbeddedInAppContext";
import { track } from "@/lib/analytics/client";
import { getDiscoverContextProps } from "@/lib/analytics/discoverContext";
import {
  type CtaIdentityProps,
  getClubIdentityProps,
  getEventIdentityProps,
} from "@/lib/analytics/identity";
import { extractEventIdFromHref } from "@/lib/analytics/tracking-utils";
import type { EventSourceType } from "@/types/discover";
import { useSearchParams } from "next/navigation";
import { useCallback } from "react";

/** `"none"` is a button that tracks its own event, such as "Contact us". */
export type CtaKind = "public_event" | "booking_form" | "club" | "none";

type CtaTrackingProps = {
  ctaKind: CtaKind;
  clubId: string | null;
  eventId?: string | null;
  groupId?: string | null;
  href: string | undefined;
  sourceType?: EventSourceType;
};

function getCtaIdentityProps({
  ctaKind,
  clubId,
  eventId,
  groupId,
  href,
  sourceType,
}: CtaTrackingProps & {
  ctaKind: Exclude<CtaKind, "none">;
}): CtaIdentityProps {
  if (ctaKind === "club") {
    return {
      ...getClubIdentityProps({ clubId, isSpondClub: true }),
      s_booking_form_id: null,
      event_id: null,
      is_public_event: false,
      is_booking_form: false,
      is_club: true,
    };
  }

  const isPublicEvent = ctaKind === "public_event";
  return {
    ...getEventIdentityProps({
      eventId: isPublicEvent
        ? (eventId ?? null)
        : (eventId ?? extractEventIdFromHref(href)),
      isPublicEvent,
      clubId,
      groupId,
      sourceType,
    }),
    external_club_id: null,
    is_booking_form: !isPublicEvent,
    is_club: false,
  };
}

export function useCtaTracking({
  ctaKind,
  clubId,
  eventId,
  groupId,
  href,
  sourceType,
}: CtaTrackingProps): { trackCtaClick: () => void } {
  const searchParams = useSearchParams();
  const { embeddedInApp } = useEmbeddedInApp();

  const trackCtaClick = useCallback(() => {
    if (ctaKind === "none") return;

    track("Click discover event call to action", {
      ...getCtaIdentityProps({
        ctaKind,
        clubId,
        eventId,
        groupId,
        href,
        sourceType,
      }),
      ...getDiscoverContextProps({
        searchParams,
        embeddedInApp,
        tab: ctaKind === "club" ? "clubs" : "events",
      }),
    });
  }, [
    searchParams,
    embeddedInApp,
    ctaKind,
    clubId,
    eventId,
    groupId,
    href,
    sourceType,
  ]);

  return { trackCtaClick };
}
